import React, { useEffect, useState } from "react";
import { apiClient } from "../../services/apiService";

interface ApiHealthStatus {
  app_name: string;
  status: "Healthy" | "Unhealthy" | "Down";
  total: number;
  working: number;
  failedServices: string[];
}

const SEARCH_KEY = "apiFilterText";

const ApiHealthCheck: React.FC = () => {
  const [apiHealth, setApiHealth] = useState<ApiHealthStatus[]>([]);
  const [filterText, setFilterText] = useState<string>(() => {
    return localStorage.getItem(SEARCH_KEY) || "";
  });
  const [error, setError] = useState<boolean>(false);

  function extractScore(score: string) {
    if (typeof score !== "string") {
      throw new Error("Input must be a string in the format 'X.YYY'");
    }

    // Split the string at the decimal point
    const [integerPart, decimalPart] = score.split(".");

    if (!integerPart || !decimalPart) {
      throw new Error("Invalid format. Expected 'X.YYY'");
    }

    return {
      totalServices: Number(integerPart),
      serviceScore: Number(decimalPart),
    };
  }

  const fetchApiHealth = async () => {
    try {
      setError(false);
      const response = await apiClient.get("/app-health");

      if (!response.data || response.data.length === 0) {
        throw new Error("No data received");
      }

      const transformedData: ApiHealthStatus[] = response.data.map(
        (api: any) => {
          const { statusCode, healthScore, failedServices } = api.metrics;

          // If API is completely down
          if (statusCode === 900) {
            return {
              app_name: api.app_name,
              status: "Down",
              total: 0,
              working: 0,
              failedServices: ["API Unreachable"],
            };
          }

          // Determine total services and serviceScore from healthScore
          const { totalServices, serviceScore } = extractScore(
            typeof healthScore === "string"
              ? healthScore
              : healthScore.toString()
          );

          const workingServices = totalServices - failedServices.length;

          // Determine the API health status
          let status: "Healthy" | "Unhealthy" | "Down";
          if (statusCode === 200) {
            status = "Healthy";
          } else if (statusCode === 503) {
            status = "Unhealthy";
          } else {
            status = "Down";
          }

          return {
            app_name: api.app_name,
            status,
            total: totalServices,
            working: workingServices,
            failedServices,
          };
        }
      );

      setApiHealth(transformedData);
    } catch (error) {
      console.error("Error fetching health data:", error);
      setError(true);
    }
  };

  useEffect(() => {
    fetchApiHealth();
    const interval = setInterval(fetchApiHealth, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.setItem(SEARCH_KEY, filterText);
  }, [filterText]);

  // 🔥 **Enhanced Filtering for Comma-Separated API Names**
  const searchTerms = filterText
    .toLowerCase()
    .split(",")
    .map((term) => term.trim())
    .filter((term) => term.length > 0);

  const filteredApiHealth = apiHealth.filter((api) =>
    searchTerms.length > 0
      ? searchTerms.some((term) => api.app_name.toLowerCase().includes(term))
      : true
  );

  const healthyApis = filteredApiHealth.filter(
    (api) => api.status === "Healthy"
  );
  const unhealthyApis = filteredApiHealth.filter(
    (api) => api.status === "Unhealthy"
  );
  const downApis = filteredApiHealth.filter((api) => api.status === "Down");

  if (error) {
    return (
      <div className="text-center text-red-600 font-medium py-4">
        ❌ Unable to fetch data. Ensure Prometheus is running.
      </div>
    );
  }

  return (
    <div>
      {/* Search Input */}
      <input
        type="text"
        placeholder="Search APIs (comma-separated)..."
        className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
      />

      <div className="overflow-x-auto bg-white shadow-md rounded-lg">
        {healthyApis.length > 0 && (
          <>
            <h3 className="text-green-700 font-semibold px-4 py-2 bg-green-100">
              ✅ Healthy APIs
            </h3>
            <table className="w-full text-sm text-left text-gray-700">
              <thead className="bg-gray-100 border-b-2 border-gray-300">
                <tr>
                  <th className="px-4 py-3">API Name</th>
                  <th className="px-4 py-3">Total Services</th>
                  <th className="px-4 py-3">Working Services</th>
                </tr>
              </thead>
              <tbody>
                {healthyApis.map((api, index) => (
                  <tr
                    key={index}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    <td className="px-4 py-3 font-medium">{api.app_name}</td>
                    <td className="px-4 py-3">{api.total}</td>
                    <td className="px-4 py-3">{api.working}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {unhealthyApis.length > 0 && (
          <>
            <h3 className="text-yellow-700 font-semibold px-4 py-2 bg-yellow-100 mt-4">
              ⚠️ Unhealthy APIs
            </h3>
            <table className="w-full text-sm text-left text-gray-700">
              <thead className="bg-gray-100 border-b-2 border-gray-300">
                <tr>
                  <th className="px-4 py-3">API Name</th>
                  <th className="px-4 py-3">Total Services</th>
                  <th className="px-4 py-3">Working Services</th>
                  <th className="px-4 py-3">Failed Services</th>
                </tr>
              </thead>
              <tbody>
                {unhealthyApis.map((api, index) => (
                  <tr
                    key={index}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    <td className="px-4 py-3 font-medium">{api.app_name}</td>
                    <td className="px-4 py-3">{api.total}</td>
                    <td className="px-4 py-3">{api.working}</td>
                    <td className="px-4 py-3 text-sm text-red-600">
                      {api.failedServices.join(", ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {downApis.length > 0 && (
          <>
            <h3 className="text-red-700 font-semibold px-4 py-2 bg-red-100 mt-4">
              ❌ Down APIs
            </h3>
            <table className="w-full text-sm text-left text-gray-700">
              <thead className="bg-gray-100 border-b-2 border-gray-300">
                <tr>
                  <th className="px-4 py-3">API Name</th>
                  <th className="px-4 py-3">Failed Services</th>
                </tr>
              </thead>
              <tbody>
                {downApis.map((api, index) => (
                  <tr
                    key={index}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    <td className="px-4 py-3 font-medium">{api.app_name}</td>
                    <td className="px-4 py-3 text-sm text-red-600">
                      {api.failedServices}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default ApiHealthCheck;
